import type { ReactNode } from 'react'
import { LandingCinematicHero, LandingViewportBreakout } from '@/components/landing/landing-cinematic-hero'
import { MarketingContentFrame } from '@/landing/marketing-content-frame'
import { marketingContentColumnProps } from '@/landing/marketing-layout-tokens'
import { cn } from '@/lib/utils'
import type { LandingHeaderStyle, LandingLayoutPreset } from '@/types/client-landing'

/** @deprecated Prefer `marketingContentColumnProps()` for tenant-aware width. */
export {
  DEFAULT_MARKETING_CONTENT_MAX_WIDTH,
  marketingContentColumnClass,
  marketingContentColumnProps,
  marketingContentColumnStyle,
  MARKETING_CONTENT_COLUMN,
  MARKETING_SHELL_X,
} from '@/landing/marketing-layout-tokens'
/** Vertical rhythm for marketing body columns (landing below-fold, public share). */
export const MARKETING_SHELL_Y = 'py-6 sm:py-8 lg:py-10'
/** Fixed sticky marketing nav height — 4.5rem (72px) from md; matches `marketing-page-header` bar. */
export const MARKETING_STICKY_NAV_CONTENT_INSET =
  'pt-[calc(4.25rem+var(--marketing-safe-top,0px))] md:pt-[calc(4.5rem+var(--marketing-safe-top,0px))]'
/**
 * Floating sticky nav (`chrome.headerStyle: floating`): 24px top gap above the pill.
 * Sticky-only — non-sticky headers ignore `headerStyle`.
 *
 * Pill is content-width + 2×`px-5` with matching `-mx-5`, so chrome overhangs the
 * column equally on both sides while logo / right actions stay flush with content.
 * (`-mx` alone on `w-full` only shifts the box left — must also widen.)
 */
export const MARKETING_STICKY_NAV_FLOATING_TOP =
  'mt-[calc(1.5rem+var(--marketing-safe-top,0px))]'
export const MARKETING_STICKY_NAV_FLOATING_PILL =
  'w-[calc(100%+2.5rem)] max-w-none -mx-5 rounded-[15px] px-5 shadow-[0_1px_2px_rgb(0_0_0/0.06)]'
/** Hero / content clearance when the sticky nav uses the floating pill. */
export const MARKETING_STICKY_NAV_CONTENT_INSET_FLOATING =
  'pt-[calc(1.5rem+5rem+var(--marketing-safe-top,0px))] md:pt-[calc(1.5rem+5rem+var(--marketing-safe-top,0px))]'
/** In-page anchor clearance under the full-bleed sticky bar. */
export const MARKETING_STICKY_NAV_SCROLL_MT =
  'scroll-mt-[calc(4.25rem+var(--marketing-safe-top,0px))] md:scroll-mt-[calc(4.5rem+var(--marketing-safe-top,0px))] outline-none'
/** In-page anchor clearance under the floating sticky pill. */
export const MARKETING_STICKY_NAV_SCROLL_MT_FLOATING =
  'scroll-mt-[calc(1.5rem+5rem+var(--marketing-safe-top,0px))] md:scroll-mt-[calc(1.5rem+5rem+var(--marketing-safe-top,0px))] outline-none'
/** Desktop-only top inset when hero background is full-bleed under a fixed overlay nav. */
export const MARKETING_STICKY_NAV_MD_INSET =
  'md:pt-[calc(4.5rem+var(--marketing-safe-top,0px))]'
/** `position: sticky` offset — nav clearance plus 1.5rem gap below the bar. */
export const MARKETING_STICKY_NAV_STICK_TOP =
  'top-[calc(4.25rem+1.5rem+var(--marketing-safe-top,0px))] md:top-[calc(4.5rem+1.5rem+var(--marketing-safe-top,0px))]'
/**
 * Mobile full-screen fold inset. Horizontal *padding* is intentional here for
 * device safe areas (notch / home indicator) — prefer margins elsewhere.
 */
export const MARKETING_MOBILE_FOLD_INSET =
  'px-5 sm:px-6 pt-[max(1.25rem,env(safe-area-inset-top))] pb-[max(2.5rem,env(safe-area-inset-bottom))]'

/** Hero pad + in-page scroll-mt for sticky nav (`chrome.headerStyle`). */
export function resolveMarketingStickyNavChrome(headerStyle: LandingHeaderStyle = 'full'): {
  contentInset: string
  scrollMt: string
} {
  if (headerStyle === 'floating') {
    return {
      contentInset: MARKETING_STICKY_NAV_CONTENT_INSET_FLOATING,
      scrollMt: MARKETING_STICKY_NAV_SCROLL_MT_FLOATING,
    }
  }
  return {
    contentInset: MARKETING_STICKY_NAV_CONTENT_INSET,
    scrollMt: MARKETING_STICKY_NAV_SCROLL_MT,
  }
}

type LandingLayoutShellProps = {
  children: ReactNode
  className?: string
}

/** Full-viewport shell only — pass **`bg-background`** or glass (**`bg-[var(--page-background)]/50 backdrop-blur-sm`**) via **`className`**. */
export function LandingLayoutRoot({ children, className }: LandingLayoutShellProps) {
  return (
    <div className={cn('relative min-h-screen text-foreground', className)}>
      {children}
    </div>
  )
}

type PresetProps = {
  hero: ReactNode
  heroMobile?: ReactNode
  heroBackground?: ReactNode
  heroAriaLabel?: string
  heroScrollBelow?: ReactNode
  splitHeroMobileFullScreen?: boolean
  splitHeroFullOpacityBackground?: boolean
  splitHeroUnderStickyNav?: boolean
  splitHeroHeight?: 'default' | '90svh'
  contentMaxWidth?: number
  belowFold?: ReactNode
  footer?: ReactNode
}

export function LandingLayoutByPreset({
  preset,
  hero,
  heroMobile,
  heroBackground,
  heroAriaLabel,
  heroScrollBelow,
  splitHeroMobileFullScreen = false,
  splitHeroFullOpacityBackground = false,
  splitHeroUnderStickyNav = false,
  splitHeroHeight = 'default',
  contentMaxWidth,
  belowFold,
  footer,
}: PresetProps & { preset: LandingLayoutPreset }) {
  const { className: contentColumn, style: contentColumnStyle } =
    marketingContentColumnProps(contentMaxWidth)
  switch (preset) {
    case 'splitHero':
      return (
        <>
          <LandingViewportBreakout>
            <LandingCinematicHero
              fullViewport
              underStickyNav={splitHeroUnderStickyNav}
              heroHeight={splitHeroHeight}
              stackBelowMd={!splitHeroMobileFullScreen}
              mobileFullScreen={splitHeroMobileFullScreen}
              mobileCopy={splitHeroMobileFullScreen ? heroMobile : hero}
              scrollBelow={splitHeroMobileFullScreen ? heroScrollBelow : undefined}
              background={heroBackground}
              ariaLabel={heroAriaLabel}
              fullOpacityBackground={splitHeroFullOpacityBackground}
              contentColumnClass={contentColumn}
              contentColumnStyle={contentColumnStyle}
            >
              {hero}
            </LandingCinematicHero>
          </LandingViewportBreakout>
          {belowFold || footer ? (
            <MarketingContentFrame
              maxWidth={contentMaxWidth}
              shellClassName={MARKETING_SHELL_Y}
              className="flex flex-col gap-10 md:gap-12"
            >
              {belowFold}
              {footer}
            </MarketingContentFrame>
          ) : null}
        </>
      )
    case 'minimal':
      return (
        <div
          className={cn(
            MARKETING_SHELL_Y,
            'mx-auto flex w-full max-w-[min(36rem,calc(100%-2rem))] flex-col gap-8 sm:max-w-[min(36rem,calc(100%-3rem))] lg:max-w-[min(36rem,calc(100%-4rem))]'
          )}
        >
          {hero}
          {belowFold}
          {footer}
        </div>
      )
    case 'centered':
    default:
      return (
        <MarketingContentFrame
          maxWidth={contentMaxWidth}
          shellClassName={MARKETING_SHELL_Y}
          className="flex flex-col items-center text-center"
        >
          <div className="flex w-full max-w-3xl flex-col gap-8">{hero}</div>
          {belowFold ? <div className="w-full text-left">{belowFold}</div> : null}
          {footer}
        </MarketingContentFrame>
      )
  }
}
